import { useRouter } from "expo-router";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoices,
} from "~/hooks/workspace";
import {
  CLIENT_ROUNDING_OPTIONS,
  invoiceRoundingLabel,
} from "~/utils/invoice-rounding";

function todayPlus(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export default function ClientsScreen() {
  const router = useRouter();
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const records = clients.data ?? [];
  const activeClients = records.filter((client) => !client.archived);

  async function createClientInvoice(clientId: string) {
    const created = await invoices.create.mutateAsync({
      clientId,
      dueDate: todayPlus(14),
      notes: "",
    });
    router.push(`/invoices/${created.id}` as never);
  }

  return (
    <ResourceListScreen
      title="Clients"
      subtitle="Manage client contact details, billing preferences, and auto-invoice settings."
      primaryAction="Add client"
      emptyTitle="No clients yet"
      emptyDescription="Add your first client so projects, time, and invoices have a billing home."
      fields={[
        { key: "name", label: "Client name", placeholder: "Aroha Studio" },
        {
          key: "contactName",
          label: "Contact name",
          placeholder: "Mereana K.",
        },
        {
          key: "email",
          label: "Billing email",
          placeholder: "accounts@example.co.nz",
          keyboardType: "email-address",
        },
        {
          key: "phone",
          label: "Phone",
          placeholder: "+64 21 000 000",
        },
        {
          key: "gstNumber",
          label: "GST number",
          placeholder: "123-456-789",
        },
        {
          key: "invoiceRoundingOverride",
          label: "Invoice rounding",
          placeholder: "Use workspace default",
          options: CLIENT_ROUNDING_OPTIONS,
          selectTitle: "Choose invoice rounding",
        },
        {
          key: "billingAddress",
          label: "Billing address",
          placeholder: "Street, suburb, city, postcode",
          multiline: true,
        },
        {
          key: "notes",
          label: "Notes",
          placeholder:
            "Billing preferences, contact notes, or follow-up details",
          multiline: true,
        },
      ]}
      requiredField="name"
      readOnly={readOnly}
      metrics={[
        { label: "Active clients", value: String(activeClients.length) },
        {
          label: "Archived",
          value: String(records.filter((client) => client.archived).length),
        },
        {
          label: "With billing email",
          value: String(records.filter((client) => client.email).length),
        },
      ]}
      items={records.map((client) => ({
        id: client.id,
        title: client.name,
        eyebrow: client.email ?? "No billing email",
        description:
          client.notes ??
          client.billing_address ??
          client.contact_name ??
          undefined,
        meta: [
          client.contact_name
            ? `Contact: ${client.contact_name}`
            : "No contact",
          client.phone ?? "No phone",
          client.gst_number ? `GST ${client.gst_number}` : "No GST number",
          invoiceRoundingLabel(client.invoice_rounding_override),
          client.archived ? "Archived" : "Active",
        ],
        editValues: readOnly
          ? undefined
          : {
              billingAddress: client.billing_address ?? "",
              contactName: client.contact_name ?? "",
              email: client.email ?? "",
              gstNumber: client.gst_number ?? "",
              invoiceRoundingOverride: client.invoice_rounding_override ?? "",
              name: client.name,
              notes: client.notes ?? "",
              phone: client.phone ?? "",
            },
        actions: (
          <>
            <Button
              compact
              iconName="external-link"
              variant="outline"
              onPress={() => router.push(`/clients/${client.id}` as never)}
            >
              <ButtonText>View details</ButtonText>
            </Button>
            {!readOnly ? (
              <>
                <Button
                  compact
                  iconName="file-plus"
                  variant="outline"
                  disabled={invoices.create.isPending}
                  onPress={() => void createClientInvoice(client.id)}
                >
                  <ButtonText>
                    {invoices.create.isPending
                      ? "Creating..."
                      : "Draft invoice"}
                  </ButtonText>
                </Button>
                <Button
                  compact
                  variant="outline"
                  disabled={clients.setArchived.isPending}
                  onPress={() =>
                    void clients.setArchived.mutateAsync({
                      archived: !client.archived,
                      id: client.id,
                    })
                  }
                >
                  <ButtonText>
                    {client.archived ? "Restore" : "Archive"}
                  </ButtonText>
                </Button>
              </>
            ) : null}
          </>
        ),
      }))}
      loading={workspace.isLoading || clients.isLoading || invoices.isLoading}
      error={
        workspace.error ??
        clients.error ??
        clients.create.error ??
        clients.update.error ??
        clients.setArchived.error ??
        invoices.error ??
        invoices.create.error
      }
      creating={clients.create.isPending}
      onCreate={(values) => clients.create.mutateAsync(values)}
      updating={clients.update.isPending}
      onUpdate={(id, values) => clients.update.mutateAsync({ id, values })}
    />
  );
}
